import React, { useRef, useCallback, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Line, getElementAtEvent } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import zoomPlugin from 'chartjs-plugin-zoom'

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, zoomPlugin)

const useStyles = makeStyles(() => ({
  chartContainer: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    height: '280px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
}))

const crosshairPlugin = {
  id: 'crosshairLine',
  afterDraw: (chart) => {
    const activeElements = chart.getActiveElements()
    if (activeElements.length === 0) return
    const { ctx } = chart
    const x = activeElements[0].element.x
    const yAxis = chart.scales.y
    if (!yAxis) return
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(x, yAxis.top)
    ctx.lineTo(x, yAxis.bottom)
    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(107, 114, 128, 0.4)'
    ctx.setLineDash([4, 4])
    ctx.stroke()
    ctx.restore()
  },
}

ChartJS.register(crosshairPlugin)

function safeDate(isoStr) {
  if (!isoStr) return null
  const d = new Date(isoStr)
  return isNaN(d.getTime()) ? null : d
}

function extractPhaseData(telemetryDocs, metricKey, phase, valueField, divisor = 1, negate = false) {
  const rawData = telemetryDocs.map((doc) => {
    const ts = safeDate(doc.receivedAt)
    if (!ts) return null
    if (doc._placeholder) return { x: ts, value: null, isPlaceholder: true }
    const arr = doc.telemetry?.[metricKey]
    const item = arr?.find((entry) => entry.channel === phase)
    if (!item || item[valueField] == null) return { x: ts, value: null, isPlaceholder: false }
    let value = Number(item[valueField]) / divisor
    if (negate) value = -value
    return { x: ts, value, isPlaceholder: false }
  }).filter(Boolean)
  const hasAnyValue = rawData.some((item) => item.value !== null && !item.isPlaceholder)
  if (!hasAnyValue) return { data: rawData.map((item) => ({ x: item.x, y: null })), wasNull: rawData.map((item) => item.isPlaceholder) }
  const data = rawData.map((item) => ({ x: item.x, y: item.value !== null ? item.value : null }))
  const wasNull = rawData.map((item) => item.isPlaceholder)
  return { data, wasNull }
}

function extractDerivedAvgPower(telemetryDocs, energyKey, energyField, phase) {
  const rawData = telemetryDocs.map((doc, index) => {
    const ts = safeDate(doc.receivedAt)
    if (!ts) return null
    if (doc._placeholder) return { x: ts, value: null, isPlaceholder: true }
    if (index === 0) return { x: ts, value: null, isPlaceholder: false }
    if (telemetryDocs[index - 1]._placeholder) return { x: ts, value: null, isPlaceholder: false }
    const currArr = doc.telemetry?.[energyKey]
    const prevArr = telemetryDocs[index - 1].telemetry?.[energyKey]
    if (!currArr || !prevArr) return { x: ts, value: null, isPlaceholder: false }
    const currChannel = currArr.find((item) => item.channel === phase)
    const prevChannel = prevArr.find((item) => item.channel === phase)
    if (!currChannel || !prevChannel) return { x: ts, value: null, isPlaceholder: false }
    const currEnergy = Number(currChannel[energyField])
    const prevEnergy = Number(prevChannel[energyField])
    if (isNaN(currEnergy) || isNaN(prevEnergy)) return { x: ts, value: null, isPlaceholder: false }
    const deltaEnergy = currEnergy - prevEnergy
    const currTime = ts.getTime() / 1000
    const prevTime = new Date(telemetryDocs[index - 1].receivedAt).getTime() / 1000
    const deltaTime = currTime - prevTime
    if (deltaTime <= 0) return { x: ts, value: null, isPlaceholder: false }
    return { x: ts, value: (deltaEnergy * 3600) / deltaTime, isPlaceholder: false }
  }).filter(Boolean)
  const hasAnyValue = rawData.some((item) => item.value !== null)
  if (!hasAnyValue) return { data: rawData.map((item) => ({ x: item.x, y: null })), wasNull: rawData.map((item) => item.isPlaceholder) }
  const data = rawData.map((item) => ({ x: item.x, y: item.value }))
  const wasNull = rawData.map((item) => item.isPlaceholder)
  return { data, wasNull }
}

function extractScalarData(telemetryDocs, metricKey) {
  const rawData = telemetryDocs.map((doc) => {
    const ts = safeDate(doc.receivedAt)
    if (!ts) return null
    if (doc._placeholder) return { x: ts, value: null, isPlaceholder: true }
    const value = doc.telemetry?.[metricKey]
    if (value == null || isNaN(Number(value))) return { x: ts, value: null, isPlaceholder: false }
    return { x: ts, value: Number(value), isPlaceholder: false }
  }).filter(Boolean)
  const hasAnyValue = rawData.some((item) => item.value !== null)
  if (!hasAnyValue) return { data: rawData.map((item) => ({ x: item.x, y: null })), wasNull: rawData.map((item) => item.isPlaceholder) }
  const data = rawData.map((item) => ({ x: item.x, y: item.value }))
  const wasNull = rawData.map((item) => item.isPlaceholder)
  return { data, wasNull }
}

function extractNetBattery(telemetryDocs, phase) {
  const rawData = telemetryDocs.map((doc) => {
    const ts = safeDate(doc.receivedAt)
    if (!ts) return null
    if (doc._placeholder) return { x: ts, value: null, isPlaceholder: true }
    const disArr = doc.telemetry?.disPMw
    const chaArr = doc.telemetry?.chaPMw
    const disItem = disArr?.find((item) => item.channel === phase)
    const chaItem = chaArr?.find((item) => item.channel === phase)
    const disPower = disItem?.power != null ? Number(disItem.power) : null
    const chaPower = chaItem?.power != null ? Number(chaItem.power) : null
    let value = null
    if (disPower != null && chaPower != null) {
      value = disPower - chaPower
    } else if (disPower != null) {
      value = disPower
    } else if (chaPower != null) {
      value = -chaPower
    }
    if (value == null || isNaN(value)) return { x: ts, value: null, isPlaceholder: false }
    return { x: ts, value: value / 1000, isPlaceholder: false }
  }).filter(Boolean)
  const hasAnyValue = rawData.some((item) => item.value !== null && !item.isPlaceholder)
  if (!hasAnyValue) return { data: rawData.map((item) => ({ x: item.x, y: null })), wasNull: rawData.map((item) => item.isPlaceholder) }
  const data = rawData.map((item) => ({ x: item.x, y: item.value }))
  const wasNull = rawData.map((item) => item.isPlaceholder)
  return { data, wasNull }
}

export function getMetricData(telemetryDocs, metric, phase) {
  if (metric.netBattery) return extractNetBattery(telemetryDocs, phase)
  if (metric.scalar) return extractScalarData(telemetryDocs, metric.key)
  if (metric.derived) return extractDerivedAvgPower(telemetryDocs, metric.energyKey, metric.energyField, phase)
  return extractPhaseData(telemetryDocs, metric.key, phase, metric.field, metric.divisor || 1, metric.negateValue || false)
}

const DLM_BAND_COLORS = {
  WATCHING:     'rgba(229, 231, 235, 0.25)',
  MODULATING:   'rgba(239, 68, 68, 0.18)',
  EXIT_PENDING: 'rgba(251, 146, 60, 0.18)',
}

try { ChartJS.unregister({ id: 'dlmOverlay' }) } catch (_) { /* first load */ }

const dlmOverlayPlugin = {
  id: 'dlmOverlay',
  beforeDatasetsDraw: (chart) => {
    const opts = chart.options.plugins?.dlmOverlay
    if (!opts) return
    const { ctx } = chart
    const xScale = chart.scales.x
    const yAxis = chart.scales.y
    if (!xScale || !yAxis) return
    const periods = opts.statePeriods || []
    for (const period of periods) {
      const fromMs = new Date(period.from).getTime()
      const toMs = new Date(period.to).getTime()
      const xLeft = xScale.getPixelForValue(fromMs)
      const xRight = xScale.getPixelForValue(toMs)
      if (Number.isNaN(xLeft) || Number.isNaN(xRight)) continue
      const color = DLM_BAND_COLORS[period.state] || DLM_BAND_COLORS.WATCHING
      ctx.save()
      ctx.fillStyle = color
      ctx.fillRect(Math.max(xLeft, xScale.left), yAxis.top, Math.min(xRight, xScale.right) - Math.max(xLeft, xScale.left), yAxis.bottom - yAxis.top)
      ctx.restore()
    }
  },
  afterDatasetsDraw: (chart) => {
    const opts = chart.options.plugins?.dlmOverlay
    if (!opts) return
    const { ctx } = chart
    const xScale = chart.scales.x
    const yAxis = chart.scales.y
    if (!xScale || !yAxis) return
    const transitions = opts.annotations || []
    for (const annotation of transitions) {
      const annotationMs = new Date(annotation.timestamp).getTime()
      const x = xScale.getPixelForValue(annotationMs)
      if (Number.isNaN(x) || x < xScale.left || x > xScale.right) continue
      ctx.save()
      ctx.beginPath()
      ctx.moveTo(x, yAxis.top)
      ctx.lineTo(x, yAxis.bottom)
      ctx.lineWidth = 2
      ctx.strokeStyle = annotation.color || '#f59e0b'
      ctx.setLineDash([6, 4])
      ctx.stroke()
      ctx.font = 'bold 9px -apple-system, sans-serif'
      ctx.fillStyle = annotation.color || '#f59e0b'
      ctx.textAlign = 'center'
      ctx.fillText(annotation.label, x, yAxis.top - 4)
      ctx.restore()
    }
  },
}

ChartJS.register(dlmOverlayPlugin)

export default function PhaseChart({ telemetryDocs, selectedMetrics, phase, numPhases, onSelectReading, onZoom, hoverInfo, onHover, dlmAnnotations, dlmStatePeriods, dlmCommandEvents, dlmDischargeSteps, importLimitWatts, sharedYRange }) {
  const classes = useStyles()
  const resettingZoom = useRef(false)
  const chartRef = useRef(null)

  const handleDblClick = useCallback((event) => {
    if (!chartRef.current || !onSelectReading) return
    const elements = getElementAtEvent(chartRef.current, event)
    if (elements.length > 0) {
      onSelectReading(elements[0].index)
    }
  }, [onSelectReading])

  const handleMouseLeave = useCallback(() => {
    if (onHover) onHover(null)
  }, [onHover])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    if (hoverInfo && hoverInfo.sourcePhase === phase) return
    if (hoverInfo && hoverInfo.index !== null) {
      const index = hoverInfo.index
      const numDatasets = chart.data.datasets.length
      const dataLength = chart.data.datasets?.[0]?.data?.length || 0
      if (numDatasets === 0 || index >= dataLength) return
      const activeElements = []
      for (let dsIndex = 0; dsIndex < numDatasets; dsIndex++) {
        activeElements.push({ datasetIndex: dsIndex, index })
      }
      chart.setActiveElements(activeElements)
      chart.tooltip.setActiveElements(activeElements, { x: 0, y: 0 })
      chart.update('none')
    } else {
      chart.setActiveElements([])
      chart.tooltip.setActiveElements([], { x: 0, y: 0 })
      chart.update('none')
    }
  }, [hoverInfo, phase])

  if (!telemetryDocs || telemetryDocs.length === 0 || selectedMetrics.length === 0) return null

  const datasets = selectedMetrics.map((metric) => {
    const { data, wasNull } = getMetricData(telemetryDocs, metric, phase)
    return {
      label: metric.shortLabel,
      data,
      borderColor: metric.color,
      backgroundColor: metric.color + '20',
      borderWidth: 1.5,
      pointRadius: (ctx) => (wasNull[ctx.dataIndex] ? 4 : 2),
      pointHoverRadius: 5,
      pointStyle: (ctx) => (wasNull[ctx.dataIndex] ? 'triangle' : 'circle'),
      pointBackgroundColor: metric.color,
      pointBorderColor: metric.color,
      pointBorderWidth: 1,
      tension: 0.3,
      spanGaps: false,
    }
  })

  if (dlmDischargeSteps && dlmDischargeSteps.length > 0 && numPhases) {
    const docTimes = telemetryDocs.map((doc) => new Date(doc.receivedAt).getTime())
    const stepData = telemetryDocs.map((doc) => ({ x: new Date(doc.receivedAt), y: null }))
    for (const step of dlmDischargeSteps) {
      const fromIndex = docTimes.findIndex((t) => t >= step.from.getTime())
      const reversedToIndex = [...docTimes].reverse().findIndex((t) => t <= step.to.getTime())
      const toIndex = reversedToIndex === -1 ? -1 : docTimes.length - 1 - reversedToIndex
      if (fromIndex === -1 || toIndex === -1 || fromIndex > toIndex) continue
      const perPhaseWatts = step.watts / numPhases
      for (let i = fromIndex; i <= toIndex; i++) {
        stepData[i].y = perPhaseWatts
      }
    }
    datasets.push({
      label: 'Cmd Discharge',
      data: stepData,
      borderColor: '#059669',
      backgroundColor: 'rgba(5, 150, 105, 0.08)',
      borderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 0,
      tension: 0,
      stepped: 'before',
      spanGaps: false,
      fill: false,
      borderDash: [],
    })
  }

  if (importLimitWatts != null) {
    datasets.push({
      label: `Import Limit (${importLimitWatts.toFixed(0)}W)`,
      data: telemetryDocs.map((doc) => ({ x: new Date(doc.receivedAt), y: importLimitWatts })),
      borderColor: '#b91c1c',
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: [8, 4],
      pointRadius: 0,
      pointHoverRadius: 0,
      tension: 0,
      fill: false,
    })
  }

  const hasData = datasets.some((dataset) => dataset.data.some((point) => point?.y != null))
  if (!hasData) return null

  const hasAnnotations = (dlmAnnotations && dlmAnnotations.length > 0) || (dlmCommandEvents && dlmCommandEvents.length > 0)
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: hasAnnotations ? { padding: { top: 14 } } : {},
    interaction: { mode: 'index', intersect: false },
    onHover: (event, elements) => {
      if (!onHover) return
      if (elements.length > 0) {
        onHover({ index: elements[0].index, sourcePhase: phase })
      } else {
        onHover(null)
      }
    },
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, pointStyle: 'line', padding: 12, font: { size: 11 }, color: '#374151' } },
      title: { display: true, text: `Phase L${phase}`, font: { size: 14, weight: '600' }, padding: { bottom: 8 }, color: '#111827' },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleFont: { size: 11 },
        bodyFont: { size: 11 },
        padding: 8,
        titleColor: '#ffffff',
        bodyColor: '#e5e7eb',
        callbacks: {
          label: (ctx) => {
            const metric = selectedMetrics[ctx.datasetIndex]
            if (!metric) {
              const value = ctx.parsed.y
              if (value === null) return null
              return `${ctx.dataset.label}: ${value.toFixed(1)} W`
            }
            const { wasNull } = getMetricData(telemetryDocs, metric, phase)
            const value = ctx.parsed.y
            if (wasNull[ctx.dataIndex]) return `${metric.shortLabel}: NULL (shown as 0) ${metric.unit}`
            if (value === null) return `${metric.shortLabel}: N/A`
            const display = metric.key === 'gridPf' ? value : value.toFixed(1)
            return `${metric.shortLabel}: ${display} ${metric.unit}`
          },
        },
      },
      zoom: {
        zoom: {
          drag: {
            enabled: true,
            backgroundColor: 'rgba(0, 188, 212, 0.1)',
            borderColor: 'rgba(0, 188, 212, 0.5)',
            borderWidth: 1,
          },
          mode: 'x',
          onZoomComplete: ({ chart }) => {
            if (!onZoom || resettingZoom.current) return
            const xScale = chart.scales.x
            const minTs = xScale.min
            const maxTs = xScale.max
            const docTimes = telemetryDocs.map((doc) => new Date(doc.receivedAt).getTime())
            const minIndex = docTimes.findIndex((t) => t >= minTs)
            const reversedMaxIdx = [...docTimes].reverse().findIndex((t) => t <= maxTs)
            const maxIndex = reversedMaxIdx === -1 ? -1 : docTimes.length - 1 - reversedMaxIdx
            resettingZoom.current = true
            chart.resetZoom()
            resettingZoom.current = false
            if (minIndex !== -1 && maxIndex !== -1 && minIndex !== maxIndex) {
              onZoom(Math.max(0, minIndex), Math.min(telemetryDocs.length - 1, maxIndex))
            }
          },
        },
      },
      dlmOverlay: {
        annotations: dlmAnnotations || [],
        statePeriods: dlmStatePeriods || [],
        commandEvents: dlmCommandEvents || [],
        docTimes: telemetryDocs.map((doc) => new Date(doc.receivedAt).getTime()),
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'minute',
          tooltipFormat: 'HH:mm:ss',
          displayFormats: { minute: 'HH:mm:ss' },
        },
        ticks: { font: { size: 10 }, maxRotation: 0, color: '#6b7280' },
        grid: { color: 'rgba(229, 231, 235, 0.8)', drawBorder: false },
      },
      y: {
        grid: { color: 'rgba(229, 231, 235, 0.8)', drawBorder: false },
        ticks: { font: { size: 10 }, color: '#6b7280' },
        ...(sharedYRange ? { min: sharedYRange.min, max: sharedYRange.max } : {}),
      },
    },
    animation: { duration: 0 },
  }

  return (
    <div className={classes.chartContainer} onMouseLeave={handleMouseLeave}>
      <Line ref={chartRef} data={{ datasets }} options={options} onDoubleClick={handleDblClick} />
    </div>
  )
}
